import { EditOutlined, MoreOutlined } from "@ant-design/icons";
import useGetDeliverableConfigurators from "@entities/deliverableConfigurator/lib/hooks/useGetDeliverableConfigurators";
import { BriefCreatorColumn } from "@features/breifCreatorColumn/BriefCreatorColumn";
import BriefContactFilterV2, {
  ContactFilterValue,
} from "@features/BriefContactFilter/BriefContactFilterV2";
import { BriefTimelinePopOver } from "@features/briefTimeline/BriefTimelinePopOver";
import { PricingDeliverableFilter } from "@features/PricingProject/ui/PricingDeliverableFilter/PricingDeliverableFilter";
import {
  IBriefInfluencerV2,
  ICost,
  IInfluencerContact,
} from "@shared/lib/types/briefInfluencerV2";
import { inrAmountWordsFromText } from "@shared/lib/utils/inrAmountInWords";
import { Button, CreatorContactDetails } from "@shared/ui";
import { BriefDeliverablePill } from "@widgets/CampaignBrief/ui/CampaignBriefTable/BriefDeliverablePill";
import { Dropdown, Flex, Space, Typography } from "antd";
import { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { useMemo, useState } from "react";
import { ClosureCreatorNotesCell } from "../ui/ClosureCreatorNotesCell";
import { ClosureDetailActionsCell } from "../ui/ClosureDetailActionsCell";
import styles from "../ui/styles.module.scss";
import {
  useClosureDetailCustomFieldColumns,
  UseClosureDetailCustomFieldColumnsParams,
} from "./useClosureDetailCustomFieldColumns";
const { Text } = Typography;

export type ContactsModalState = {
  visible: boolean;
  briefInfluencerId: string;
  conatcts: IInfluencerContact[];
  addContactOnly?: boolean;
  initialContact: IInfluencerContact | null;
  creatorInfo:
    | {
        name: string;
        dp: string;
        rating: number;
      }
    | undefined;
};

export type DeliverableModalState = {
  visible: boolean;
  deliverableId: string;
  briefInfluencerIds: string[];
  type: "view" | "create";
};

type SelectedInfluencerState = {
  visible: boolean;
  props?: {
    influencerId?: string;
    username?: string;
    platform?: string;
    toOpenInfluencer?: boolean;
    creatorInfo: {
      name?: string;
      image?: string;
      username?: string;
    };
  };
};

type UseClosureDetailColumnsParams = {
  briefId: string;
  onActionSuccess: () => void;
  setContactsModalProps: React.Dispatch<
    React.SetStateAction<ContactsModalState>
  >;
  setSelectedInfluencer: React.Dispatch<
    React.SetStateAction<SelectedInfluencerState>
  >;
  contactFilter: ContactFilterValue;
  setContactFilter: React.Dispatch<React.SetStateAction<ContactFilterValue>>;
  isContactFilterActive: boolean;
  deliverableFilter: string[];
  setDeliverableFilter: React.Dispatch<React.SetStateAction<string[]>>;
  briefInfluencers?: IBriefInfluencerV2[];
} & Pick<
  UseClosureDetailCustomFieldColumnsParams,
  "sortBy" | "setSortBy" | "dynamicFields" | "setDynamicField"
>;

function formatPocDate(value?: string): string {
  if (!value) {
    return "—";
  }

  const date = dayjs(value);
  return date.isValid() ? date.format("MMM D, YYYY HH:mm") : "—";
}

function ClosureCommercialCell({ cost }: { cost?: ICost }) {
  if (!cost) {
    return (
      <div className={styles.commercialCell}>
        <span className={styles.emptyPlaceholder}>—</span>
      </div>
    );
  }

  const amountWords = inrAmountWordsFromText(cost.cost);

  return (
    <div className={styles.commercialCell}>
      <span className={styles.brandCommercialAmount}>₹{cost.cost}</span>
      {/* <span className={styles.brandCommercialMeta}>
        {cost.createdAt ? formatDate(cost.createdAt, "dd/MM/yy") : "—"}{" "}
        {cost.createdBy?.firstName}
      </span> */}
      {amountWords ? (
        <span className={styles.commercialWords}>{amountWords}</span>
      ) : null}
    </div>
  );
}

export function useClosureDetailColumns({
  briefId,
  onActionSuccess,
  setContactsModalProps,
  setSelectedInfluencer,
  contactFilter,
  setContactFilter,
  isContactFilterActive,
  deliverableFilter,
  setDeliverableFilter,
  sortBy,
  setSortBy,
  dynamicFields,
  setDynamicField,
  briefInfluencers = [],
}: UseClosureDetailColumnsParams): ColumnsType<IBriefInfluencerV2> {
  const customFieldColumns = useClosureDetailCustomFieldColumns({
    briefId,
    briefInfluencers,
    sortBy,
    setSortBy,
    dynamicFields,
    setDynamicField,
    onFieldEditSuccess: onActionSuccess,
  });
  const { deliverableConfiguratorsData } = useGetDeliverableConfigurators();
  const configurators = deliverableConfiguratorsData?.data?.data || [];
  const [editingNotesId, setEditingNotesId] = useState<string | null>(null);
  const openChangeContact = (record: IBriefInfluencerV2) => {
    if (record?.influencer?.contacts && record.influencer.contacts.length > 0) {
      setContactsModalProps({
        initialContact: record?.primaryContact || null,
        addContactOnly: true,
        visible: true,
        briefInfluencerId: record._id,
        conatcts: record.influencer.contacts,
        creatorInfo: {
          name: record.socialMedia.username,
          dp: record.socialMedia?.profilePicUrl,
          rating: record.influencer?.avgReview || 0,
        },
      });
      return;
    }

    setSelectedInfluencer({
      visible: true,
      props: {
        influencerId: record?.influencerId || "",
        username: record.socialMedia?.username,
        platform: record.socialMedia?.platform || "",
        creatorInfo: {
          name: record.influencerName,
          image: record.socialMedia?.profilePicUrl,
          username: record.socialMedia?.username,
        },
        toOpenInfluencer: true,
      },
    });
  };

  const renderContactDropdown = (record: IBriefInfluencerV2) => (
    <Dropdown
      menu={{
        items: [
          {
            key: "change",
            label: "Change Contact",
            icon: <EditOutlined />,
            onClick: () => openChangeContact(record),
          },
        ],
      }}
      trigger={["click", "hover"]}
      placement="bottomRight"
    >
      <Button icon={<MoreOutlined />} type="text" />
    </Dropdown>
  );

  return useMemo(
    () => [
      {
        title: "Creator",
        key: "creator",
        fixed: "left",
        width: 170,
        render: (_, record) => (
          <BriefCreatorColumn
            influencerId={record.influencerId}
            influencerName={record.influencerName}
            username={record.socialMedia?.username}
            platform={record.socialMedia?.platform}
            profilePicUrl={record.socialMedia?.profilePicUrl}
            link={record.socialMedia?.link}
            isCreatorDeclined={record.isCreatorDeclined}
            isBrandDeclined={record.isBrandDeclined}
            creatorDeclinedReason={record.creatorDeclinedReason}
            brandDeclinedReason={record.brandDeclinedReason}
          />
        ),
      },
      {
        title: "Creator contact",
        key: "contact",
        width: 220,
        filterCount: isContactFilterActive ? 1 : 0,
        filterDropdownProps: {
          destroyPopupOnHide: true,
        },
        filtered: isContactFilterActive,
        filterDropdown: (props: { confirm: () => void; close: () => void }) => (
          <BriefContactFilterV2
            {...props}
            value={contactFilter}
            onApply={setContactFilter}
          />
        ),
        render: (_, record: IBriefInfluencerV2) => {
          return (
            <Flex
              gap={5}
              justify="space-between"
              align="center"
              style={{ width: "100%" }}
            >
              <CreatorContactDetails
                contact={record.primaryContact}
                isContactAdded={record.isPrimaryContactAdded}
                isCompact={false}
              />
              {record.isPrimaryContactAdded
                ? renderContactDropdown(record)
                : null}
            </Flex>
          );
        },
      },
      {
        title: "Deliverables",
        key: "deliverables",
        width: 200,
        filterCount: deliverableFilter?.length || 0,
        filterDropdownProps: {
          destroyPopupOnHide: true,
        },
        filtered: !!deliverableFilter?.length,
        filterDropdown: (props: { confirm: () => void; close: () => void }) => (
          <PricingDeliverableFilter
            briefId={briefId || ""}
            confirm={props.confirm}
            close={props.close}
            selectedValues={deliverableFilter}
            onApply={setDeliverableFilter}
          />
        ),
        render: (_, record) => {
          if (record.isDeliverablesPending) {
            return <span> Pending</span>;
          }

          if (
            record?.deliverableV2?.purchaseItems &&
            record.deliverableV2.purchaseItems.length > 0
          ) {
            return (
              <Flex align="center" gap={2}>
                <span className={styles.deliverableSummary}>
                  {record.deliverableV2 ? (
                    <BriefDeliverablePill
                      deliverable={record.deliverableV2}
                      configurators={configurators}
                      showSpecialNotes
                    />
                  ) : null}
                </span>
              </Flex>
            );
          }

          return <span className={styles.emptyPlaceholder}>—</span>;
        },
      },

      {
        title: "Creator commercials",
        key: "creatorCommercials",
        width: 200,
        render: (_, record) => (
          <Space size={4} align="start">
            <BriefTimelinePopOver briefInfluencer={record} type="influencer" />
            <ClosureCommercialCell cost={record.influencerCost} />
          </Space>
        ),
      },
      {
        title: "Brand commercials",
        key: "brandCommercials",
        width: 200,
        render: (_, record) => (
          <Space size={4} align="start">
            <BriefTimelinePopOver briefInfluencer={record} type="brand" />
            <ClosureCommercialCell cost={record.brandCost} />
          </Space>
        ),
      },
      ...customFieldColumns,
      {
        title: "Creator Notes",
        key: "notes",
        width: 200,
        fixed: "right",
        render: (_, record) => (
          <ClosureCreatorNotesCell
            record={record}
            isEditing={editingNotesId === record._id || !record.pricingNotes}
            onStartEdit={() => setEditingNotesId(record._id)}
            onClose={() => setEditingNotesId(null)}
            onSuccess={() => {
              onActionSuccess();
              setEditingNotesId(null);
            }}
          />
        ),
      },
      {
        title: "Actions",
        key: "actions",
        align: "right",
        width: 240,
        fixed: "right",
        render: (_, record) => (
          <ClosureDetailActionsCell
            record={record}
            briefId={briefId}
            onActionSuccess={onActionSuccess}
          />
        ),
      },
    ],
    [
      briefId,
      onActionSuccess,
      customFieldColumns,
      setContactsModalProps,
      setSelectedInfluencer,
      contactFilter,
      setContactFilter,
      isContactFilterActive,
      deliverableFilter,
      setDeliverableFilter,
      editingNotesId,
    ]
  );
}
